/**
 * Continuous Improvement — shared threshold logic.
 *
 * This is the single source of truth for CI retrain thresholds.
 * Both the backend (continuousImprovementService) and the Rapid frontend
 * import from here so the schedule stays in sync.
 */

/**
 * Threshold schedule: as datasets grow, the spacing between retrain
 * triggers increases so training costs stay proportional.
 *
 * | Image range | Increment | Example thresholds               |
 * |-------------|-----------|----------------------------------|
 * | 0–100       | variable  | 10, 20, 40, 60, 80, 100         |
 * | 100–500     | 50        | 150, 200, 250, …, 500           |
 * | 500–1 000   | 100       | 600, 700, …, 1 000              |
 * | 1 000–2 000 | 200       | 1 200, 1 400, …, 2 000          |
 * | 2 000–3 000 | 250       | 2 250, 2 500, 2 750, 3 000      |
 * | 3 000+      | 500       | 3 500, 4 000, 4 500, …          |
 */

/** Fixed thresholds for the 0-100 range. */
const SMALL_DATASET_THRESHOLDS = Object.freeze([10, 20, 40, 60, 80, 100]);

/**
 * Returns the next CI retrain threshold above the given image count.
 *
 * @param {number} images - Current image count
 * @returns {number} The next threshold above the given count
 */
function getNextThreshold(images) {
    if (images < 10) return 10;

    if (images < 100) {
        for (var i = 0; i < SMALL_DATASET_THRESHOLDS.length; i++) {
            if (images < SMALL_DATASET_THRESHOLDS[i]) return SMALL_DATASET_THRESHOLDS[i];
        }
        return 150;
    }

    if (images < 500) {
        return Math.ceil((images + 1) / 50) * 50;
    }

    if (images < 1000) {
        return Math.ceil((images + 1) / 100) * 100;
    }

    if (images < 2000) {
        return Math.ceil((images + 1) / 200) * 200;
    }

    if (images < 3000) {
        return Math.ceil((images + 1) / 250) * 250;
    }

    return Math.ceil((images + 1) / 500) * 500;
}

/**
 * Returns the largest CI retrain threshold that is <= the given image count.
 * Useful for computing segment progress (how far between the last and next threshold).
 *
 * @param {number} images - Current image count
 * @returns {number} The largest threshold at or below the given count
 */
function getPreviousThreshold(images) {
    if (images < 10) return 0;

    if (images < 100) {
        var prev = 0;
        for (var i = 0; i < SMALL_DATASET_THRESHOLDS.length; i++) {
            if (images < SMALL_DATASET_THRESHOLDS[i]) return prev;
            prev = SMALL_DATASET_THRESHOLDS[i];
        }
        return 100;
    }

    if (images < 500) {
        return Math.floor(images / 50) * 50;
    }

    if (images < 1000) {
        return Math.floor(images / 100) * 100;
    }

    if (images < 2000) {
        return Math.floor(images / 200) * 200;
    }

    if (images < 3000) {
        return Math.floor(images / 250) * 250;
    }

    return Math.floor(images / 500) * 500;
}

/**
 * Checks if the image count crossed a threshold in the upward direction.
 * A threshold is crossed when: beforeImages < threshold <= afterImages.
 *
 * @param {number} beforeImages - Image count before the update
 * @param {number} afterImages  - Image count after the update
 * @returns {boolean} true if a threshold was crossed going up
 */
function didCrossThreshold(beforeImages, afterImages) {
    if (afterImages <= beforeImages) {
        return false;
    }
    var nextThreshold = getNextThreshold(beforeImages);
    return afterImages >= nextThreshold;
}

module.exports = {
    SMALL_DATASET_THRESHOLDS: SMALL_DATASET_THRESHOLDS,
    getNextThreshold: getNextThreshold,
    getPreviousThreshold: getPreviousThreshold,
    didCrossThreshold: didCrossThreshold
};
